<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ mode === 'reprice'
            ? $t('HeadContract.VariationsSchedule.VariationShowModal.repriceConfirmTitle')
            : $t('HeadContract.VariationsSchedule.PricingModal.title', { code: variationOrder.code }) }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="min-w-md flex flex-col overflow-y-auto w-full">
        <div class="flex justify-between space-x-4 items-center">
          <div class="max-w-md text-wrap">
            <span class="font-semibold">
              {{ $t('HeadContract.VariationsSchedule.PricingModal.description') }}
            </span>

            <p>
              {{ variationOrder.description }}
            </p>
          </div>

          <button
            v-if="project.type !== 'client'"
            class="btn btn--secondary-blue"
            type="button"
            @click="togglePricing"
          >
            {{ totalSumSelected ? $t('HeadContract.VariationsSchedule.PricingModal.button.priceByBoQ') : $t('HeadContract.VariationsSchedule.PricingModal.button.priceBySum') }}
          </button>
        </div>

        <div
          v-if="variationOrder.notes"
          class="mt-2 max-w-md text-wrap"
        >
          <span class="font-semibold">
            {{ $t('HeadContract.VariationsSchedule.PricingModal.notes') }}
          </span>

          <p>
            {{ variationOrder.notes }}
          </p>
        </div>

        <form
          action=""
          class="flex flex-col space-y-4 mt-4"
          @submit.prevent=""
        >
          <div
            v-if="!falsey(pricingForm.form.trade_items)"
            class="flex flex-col overflow-auto"
          >
            <div class="flex space-x-4 mt-4">
              <div class="flex flex-col space-y-2 w-1/2">
                <div class="text-lg font-semibold">
                  {{ !falsey(selectedTradeItem) ? `Edit ${pricingForm.form.trade_items[selectedTradeItem].name}` : 'Trade Item Details' }}
                </div>

                <label>
                  {{ $t('HeadContract.VariationsSchedule.PricingModal.label.name') }}
                  <AppInput
                    v-model="tradeItemForm.form.name"
                    name="name"
                    type="text"
                    class="form-input"
                    :error="tradeItemForm.getErrors('name')"
                    data-test="name-input"
                  />
                </label>

                <div class="flex space-x-4">
                  <label>
                    {{ $t('HeadContract.VariationsSchedule.PricingModal.label.unit') }}
                    <UnitsTypeAhead
                      v-model="tradeItemForm.form.unit"
                      name="unit"
                      :error="tradeItemForm.getErrors('unit')"
                      data-test="unit-input"
                    />
                    <!-- <AppInput
                      v-model="tradeItemForm.form.unit"
                      class="form-input"
                      name="unit"
                      type="text"
                      :error="tradeItemForm.getErrors('unit')"
                    /> -->
                  </label>

                  <label>
                    {{ $t('HeadContract.VariationsSchedule.PricingModal.label.quantity') }}
                    <AppInput
                      v-model="tradeItemForm.form.quantity"
                      class="form-input"
                      name="quantity"
                      type="number"
                      :error="tradeItemForm.getErrors('quantity')"
                      data-test="quantity-input"
                    />
                  </label>
                </div>

                <label>
                  {{ $t('HeadContract.VariationsSchedule.PricingModal.label.rate') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }}/{{ $t('HeadContract.VariationsSchedule.PricingModal.hint.unit') }})</span>

                  <AppCurrencyInput
                    v-model="tradeItemForm.form.rate"
                    class="form-input"
                    name="rate"
                    :error="tradeItemForm.getErrors('rate')"
                    data-test="rate-input"
                  />
                </label>

                <div class="flex space-x-4 items-end">
                  <label class="w-1/2">
                    {{ $t('HeadContract.VariationsSchedule.PricingModal.label.budget') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }})</span>

                    <AppCurrencyInput
                      v-model="tradeItemForm.form.budget"
                      class="form-input disabled:bg-gray-300"
                      name="budget"
                      :error="tradeItemForm.getErrors('budget')"
                      data-test="budget-input"
                      disabled
                    />
                  </label>

                  <button
                    type="button"
                    class="btn btn--secondary-blue w-1/2 disabled:cursor-not-allowed"
                    :disabled="!canAddTradeItem"
                    @click="!falsey(selectedTradeItem) ? saveEditTradeItem() : pushTradeItem()"
                  >
                    {{ !falsey(selectedTradeItem) ? $t('HeadContract.VariationsSchedule.PricingModal.button.saveTradeItem', { name: pricingForm.form.trade_items[selectedTradeItem].name }) : $t('HeadContract.VariationsSchedule.PricingModal.button.addItem') }}
                  </button>
                </div>
              </div>

              <div class="w-1/2">
                <div class="text-lg font-semibold px-2 flex justify-between">
                  <span>
                    {{ $t('HeadContract.VariationsSchedule.PricingModal.variationBillOfQuantities') }}
                  </span>

                  <span
                    v-if="tradeItemTotal > 0"
                    class="font-normal"
                  >
                    {{ australianCurrency(tradeItemTotal) }}
                  </span>
                </div>

                <table class="w-full">
                  <thead>
                    <tr class="first-child:pl-2 last-child:pr-2 child:p-2 text-left border-b border-gray-300">
                      <th>
                        {{ $t('HeadContract.VariationsSchedule.PricingModal.table.item') }}
                      </th>

                      <th>
                        {{ $t('HeadContract.VariationsSchedule.PricingModal.table.unit') }}
                      </th>

                      <th class="text-right">
                        {{ $t('HeadContract.VariationsSchedule.PricingModal.table.quantity') }}
                      </th>

                      <th class="text-right">
                        {{ $t('HeadContract.VariationsSchedule.PricingModal.table.rate') }}
                      </th>

                      <th class="text-right">
                        {{ $t('HeadContract.VariationsSchedule.PricingModal.table.amount') }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(item, index) in pricingForm.form.trade_items"
                      :key="index"
                      class="child:p-2 odd:bg-white even:bg-gray-100"
                    >
                      <td :title="item.name">
                        <div class="flex space-x-2">
                          <button
                            type="button"
                            class="text-red-600"
                            @click="removeTradeItem(index)"
                          >
                            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-6 h-6">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                          </button>

                          <button
                            type="button"
                            class="text-ccm-blue-500"
                            @click="editTradeItem(index)"
                          >
                            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-6 h-6">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                          </button>

                          <p class="text-wrap">
                            {{ item.name }}
                          </p>
                        </div>
                      </td>

                      <td>
                        {{ item.unit }}
                      </td>

                      <td class="text-right">
                        {{ $tn(item.quantity) }}
                      </td>

                      <td class="text-right">
                        {{ $tcc(item.rate) }}/{{ item.unit }}
                      </td>

                      <td class="text-right">
                        {{ $tcc(item.budget) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            v-else
            class="flex flex-col"
          >
            <label class="mt-4">
              <span class="font-semibold text-lg">
                {{ $t('HeadContract.VariationsSchedule.PricingModal.priceVariationBySum') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }})</span>
              </span>

              <AppCurrencyInput
                v-model="pricingForm.form.budget"
                name="budget"
                class="form-input mt-2"
                :error="pricingForm.getErrors('budget')"
              />
            </label>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.PricingModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          @click="savePricing"
        >
          {{ $t('HeadContract.VariationsSchedule.PricingModal.button.save') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import UnitsTypeAhead from '@/components/UnitsTypeAhead.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, dateForDateInput, falsey } from '@/helpers';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const emit = defineEmits<{
  (e: 'close');
  (e: 'priced');
}>();

const props = withDefaults(
  defineProps<{
    variationOrderId: Id;
    mode?: 'price' | 'reprice';
  }>(),
  {
    mode: 'price',
  },
);

const variationsOrdersStore = useVariationOrdersStore();

const variationOrder = computed(() => {
  return variationsOrdersStore.models.with('trade_items').find(props.variationOrderId);
});

const pricingForm = useFormData({
  budget: 0,
  trade_items: undefined,
});

const totalSumSelected = ref(true);

watch(variationOrder, (newValue) => {
  if(newValue) {
    if(newValue.trade_items.length) {
      totalSumSelected.value = false;
      pricingForm.form.budget = undefined;

      const tradeItems = newValue.trade_items.map((tradeItem) => {
        return {
          name: tradeItem.name,
          budget: tradeItem.budget,
          rate: tradeItem.rate,
          quantity: tradeItem.quantity,
          unit: tradeItem.unit,
        };
      });

      pricingForm.form.trade_items = tradeItems;
    } else {
      pricingForm.setData(newValue, ['trade_items']);
    }
  }
}, { immediate: true });

const tradeItemForm = useFormData({
  name: '',
  budget: 0,
  rate: 0,
  quantity: 0,
  unit: '',
});

const togglePricing = () => {
  if(totalSumSelected.value) {
    totalSumSelected.value = false;

    pricingForm.form.trade_items = [];

    pricingForm.form.budget = undefined;
  } else {
    if(pricingForm.form.trade_items?.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Switch to Total Sum?',
        text: 'Switching will clear any trade items created',
        showCancelButton: true,
      }).then((result) => {
        if(result.isConfirmed) {
          totalSumSelected.value = true;

          pricingForm.resetData();
        }
      });
    } else {
      totalSumSelected.value = true;

      pricingForm.resetData();
    }
  }
};

const variationOrderPricingAction = useStoreApiAction(variationsOrdersStore.priceVariationOrder);
const variationOrderRepriceAction = useStoreApiAction(variationsOrdersStore.repriceVariationOrder);

const savePricing = () => {
  if(props.mode === 'reprice') {
    Swal.fire({
      title: t('HeadContract.VariationsSchedule.VariationShowModal.repriceConfirmTitle'),
      icon: 'question',
      showCancelButton: true,
      input: 'textarea',
      inputLabel: t('HeadContract.VariationsSchedule.VariationShowModal.repriceCommentLabel'),
      inputValidator: (value) => {
        if(!value?.trim()) {
          return t('HeadContract.VariationsSchedule.VariationShowModal.repriceCommentRequired');
        }
      },
    }).then((result) => {
      if(!result.isConfirmed) {
        return;
      }

      variationOrderRepriceAction.request(props.variationOrderId, {
        ...pricingForm.form,
        comment: result.value.trim(),
      }).then(() => {
        emit('priced');

        Swal.fire(t('HeadContract.VariationsSchedule.VariationShowModal.repriceSuccess'), '', 'success').then(() => {
          emit('close');
        });
      }).catch((error) => {
        pricingForm.setErrorsFromAxiosError(error);
      });
    });

    return;
  }

  variationOrderPricingAction.request(props.variationOrderId, pricingForm.form).then((data) => {
    emit('priced');

    Swal.fire('Variation Priced', '', 'success').then(() => {
      emit('close');
    });
  }).catch((error) => {
    console.log(error);
  });
};

watch(() => [tradeItemForm.form.rate, tradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] > 0 && newValue[1] > 0) {
    tradeItemForm.form.budget = newValue[0] * newValue[1];
  }
});

const pushTradeItem = () => {
  pricingForm.form.trade_items.push({
    ...tradeItemForm.form,
  });

  tradeItemForm.resetData();
};

const tradeItemTotal = computed(() => {
  return pricingForm.form.trade_items?.reduce((total, tradeItem) => {
    return total += tradeItem.budget;
  }, 0);
});

const canAddTradeItem = computed(() => {
  return !!(tradeItemForm.form.name && tradeItemForm.form.budget && tradeItemForm.form.quantity &&
    tradeItemForm.form.rate && tradeItemForm.form.unit);
});

const removeTradeItem = (index: number) => {
  Swal.fire({
    icon: 'question',
    text: 'Remove trade item?',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      pricingForm.form.trade_items.splice(index, 1);
    }
  });
};

const selectedTradeItem = ref<number | undefined>();

const editTradeItem = (index: number) => {
  selectedTradeItem.value = index;

  tradeItemForm.setData(pricingForm.form.trade_items[index]);
};

const saveEditTradeItem = () => {
  pricingForm.form.trade_items[selectedTradeItem.value] = {
    ...tradeItemForm.form,
  };

  tradeItemForm.resetData();

  selectedTradeItem.value = undefined;
};

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});
</script>

<style scoped></style>
